/* eslint-disable no-console */
import jss from 'jss';
import jssPluginGlobal from 'jss-plugin-global';
import jssPluginCamelCase from 'jss-plugin-camel-case';
import jssPluginNested from 'jss-plugin-nested';
import merge from 'lodash/merge';
import startsWith from 'lodash/startsWith';

jss.use(jssPluginNested());
jss.use(jssPluginGlobal());
jss.use(jssPluginCamelCase());

// #FFF to #FFFFFF
function shortToLongHex(hex) {
    const [r, g, b] = [hex[1], hex[2], hex[3]];
    return `#${r}${r}${g}${g}${b}${b}`;
}

function getRgb(hexCode) {
    let hex = hexCode;
    if (hexCode.length === 3 || hexCode.length === 6) {
        hex = `#${hex}`;
    }
    if (hex.length === 4) {
        hex = shortToLongHex(hex);
    }
    const [r, g, b] = [
        parseInt(hex.slice(1, 3), 16),
        parseInt(hex.slice(3, 5), 16),
        parseInt(hex.slice(5, 7), 16)
    ];
    return {
        r,
        g,
        b,
    };
}

export function hexToRgb(hex) {
    const rgb = getRgb(hex);
    return `rgb(${rgb.r},${rgb.g},${rgb.b})`;
}

export function hexToRgba(hex, o = 1) {
    const rgb = getRgb(hex);
    return `rgb(${rgb.r},${rgb.g},${rgb.b}, ${o})`;
}

export function getDarkFactor(r, g, b) {
    let obj = {
        r,
        g,
        b,
    };
    if (r && typeof g === 'undefined') {
        obj = getRgb(r);
        // hex code is provided
    }
    // Counting the perceptive luminance
    // human eye favors green color...
    return 1 - (0.299 * obj.r + 0.587 * obj.g + 0.114 * obj.b) / 255;
}

function isBright(r, g, b) {
    return getDarkFactor(r, g, b) < 0.9;
}

/**
 * gets an rgb string (rgb(0, 0, 0)) and returns an object with the RGB values
 * @param  {string} color an RGB string - rgb(0, 0, 0)
 */
function getRgbValuesFromRgb(color) {
    const rgbArray = color.match(/rgb\(([0-9]+),[ ]*([0-9]+),[ ]*([0-9]+)\)/);

    return {
        R: parseInt(rgbArray[1], 10),
        G: parseInt(rgbArray[2], 10),
        B: parseInt(rgbArray[3], 10),
    };
}

export function makeASlightlyDifferentColor(c, percent = 0.05, opacity = 1) {
    if (!c) {
        return '#ffffff';
    }
    const color = hexToRgb(c);
    const { R, G, B } = getRgbValuesFromRgb(color);

    // How dark the color is?
    const darkFactor = getDarkFactor(R, G, B);
    let p = percent;
    if (isBright(R, G, B)) {
        p *= -0.6;
    }

    // The darker color is, the bigger percent we need to see the difference,
    // I.E rgb(255,255,255) vs rgb(240,240,240) - a visible changes, while
    // rbg(0,0,0) vs rgb(15,15,15) - absolutely no difference
    p *= Math.exp(darkFactor * 1.2);

    const newR = Math.min(Math.max(Math.round(255 * p) + R, 0), 255);
    const newG = Math.min(Math.max(Math.round(255 * p) + G, 0), 255);
    const newB = Math.min(Math.max(Math.round(255 * p) + B, 0), 255);

    return `rgba(${newR}, ${newG}, ${newB}, ${opacity})`;
}

export function wrapObjectWithSelector(styleObject, selector) {
    return Object.keys(styleObject)
        .reduce((newRules, key) => {
            if (startsWith(key, '@keyframe')) {
                newRules[key] = styleObject[key];
                return newRules;
            }
            const keys = key.split(',');
            keys.forEach((singleKey) => {
                if (newRules[`${selector} ${singleKey.trim(' ')}`]) {
                    newRules[`${selector} ${singleKey.trim(' ')}`] = merge(
                        {},
                        newRules[`${selector} ${singleKey.trim(' ')}`],
                        styleObject[key]
                    );
                } else {
                    newRules[`${selector} ${singleKey.trim(' ')}`] = styleObject[key];
                }
            });
            return newRules;
        }, {});
}

/**
 Allows to write nested selector without '& ' prefix
 * @param obj
 * @private
 * @return {*}
 */
function modifySelectors(obj) {
    const newObj = {};
    Object.keys(obj)
        .forEach((key) => {
            if (typeof obj[key] === 'object' && !startsWith(key, '&')) {
                newObj[`& ${key}`] = modifySelectors(obj[key]);
            } else if (typeof obj[key] === 'object') {
                newObj[key] = modifySelectors(obj[key]);
            } else {
                newObj[key] = obj[key];
            }
        });
    return newObj;
}

/**
 Allows to write nested selector without '& ' prefix
 * @param styleObj
 * @private
 * @return {*}
 */
function modifyStyleObject(styleObj) {
    const newObj = {};
    Object.keys(styleObj)
        .forEach(key => {
            if (startsWith(key, '@keyframe')) {
                newObj[key] = styleObj[key];
            } else {
                newObj[key] = modifySelectors(styleObj[key]);
            }
        });

    return newObj;
}

/**
 * @description helper to convert JS object into css styles
 * @param {{".global-filters": {backgroundColor}, ".global-filters .auto-updates-host": {color},
 * ".global-filters .filters-title": {color}}} styleObject - object with styles description
 * @param conf - config object
 * @param { Object } styleObject.cssSelector - object representing css selector with rules
 * @param { string } styleObject.cssSelector.rule  - css rule
 * @return { string } - object converted to string with css styles
 */
export function convertStyleObjToCss(_styleObject, conf = {}) {
    const styleObject = modifyStyleObject(_styleObject);
    let styles = styleObject;
    try {
        if (conf.wrapper) {
            styles = wrapObjectWithSelector(styleObject, conf.wrapperClass || '.sis-scope');
        }
        const sheet = jss.createStyleSheet({
            '@global': styles,
        });
        return sheet.toString();
    } catch (e) {
        console.error('Can\'t generate style for module, error: ', e.toString());
        return '';
    }
}

